import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { useDesiredVideoPlatform, useVideoPlatform } from '../hooks/useVideoPlatform';
import { recordJourneyStep, setJourneyContext, setJourneyUserType } from '../journey/journeySteps';
import { useJourneyPageExit } from '../journey/useJourneyPageExit';
import { syncClientConnectionStateAtom } from '../session/state';
import { ConnectionState } from '../session/types';
import { sessionInfoAtom } from '../state';
import { numWaitingVisitorsAtom, providerShortIdAtom, visitorPresenceMapSidAtom } from './state';

/** Provider arriving steps and waiting-room "seen"; the rest come from tracking events. */
export function useProviderJourneySteps() {
  const providerShortId = useAtomValue(providerShortIdAtom);
  const syncConnectionState = useAtomValue(syncClientConnectionStateAtom);
  const presenceMapSid = useAtomValue(visitorPresenceMapSidAtom);
  const numWaitingVisitors = useAtomValue(numWaitingVisitorsAtom);
  const appointmentShortId = useAtomValue(sessionInfoAtom)?.appointment?.shortId;
  const previousWaitingRef = useRef(0);
  // desired platform until the adapter exists
  const videoPlatform = useVideoPlatform();
  const desiredVideoPlatform = useDesiredVideoPlatform();

  // before recordLoad so the first event carries the context
  useEffect(
    function setEventContext() {
      setJourneyContext({
        video_platform: videoPlatform ?? desiredVideoPlatform,
        appointment_short_id: appointmentShortId,
      });
    },
    [videoPlatform, desiredVideoPlatform, appointmentShortId],
  );

  useEffect(function recordLoad() {
    setJourneyUserType('provider');
    recordJourneyStep('arriving.load');
  }, []);

  useJourneyPageExit();

  useEffect(
    function recordIdentify() {
      if (providerShortId) recordJourneyStep('arriving.identify', { authenticated: true });
    },
    [providerShortId],
  );

  useEffect(
    function recordReady() {
      if (syncConnectionState === ConnectionState.CONNECTED && presenceMapSid) recordJourneyStep('arriving.ready');
    },
    [syncConnectionState, presenceMapSid],
  );

  useEffect(
    function recordSeen() {
      if (numWaitingVisitors > previousWaitingRef.current) {
        recordJourneyStep('waiting.seen', { repeatable: true, waiting_visitors: numWaitingVisitors });
      }
      previousWaitingRef.current = numWaitingVisitors;
    },
    [numWaitingVisitors],
  );
}
